import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useModalScrollLock } from "../hooks/useModalScrollLock";

interface ImageLightboxProps {
  isOpen: boolean;
  src: string | null;
  alt?: string;
  onClose: () => void;
}

export default function ImageLightbox({
  isOpen,
  src,
  alt = "Image Preview",
  onClose,
}: ImageLightboxProps) {
  useModalScrollLock(isOpen);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !src) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="relative max-w-4xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col items-center justify-center p-2"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-black/60 hover:bg-black text-white rounded-full transition-colors"
          title="Close preview"
        >
          <X size={20} />
        </button>

        <div className="relative w-full h-[65vh] md:h-[75vh] flex items-center justify-center bg-gray-950 rounded-xl overflow-hidden">
          <img
            src={src}
            alt={alt}
            className="max-w-full max-h-full object-contain"
          />
        </div>

        {alt && (
          <div className="py-3 px-4 text-center text-sm font-semibold text-[#6D3A0D] w-full truncate">
            {alt}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
